import { Link } from "react-router-dom";
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { Ppp } from "@/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { UserAvatar } from "@/components/UserAvatar";
import { cn } from "@/lib/utils";
import { AlertTriangle, GripVertical, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { PppLock } from "./PppLock";
import { PPP_CATEGORY_LABEL, PPP_ROW_COLS, PPP_TYPE_BADGE, PPP_TYPE_DOT } from "./ppp-constants";
import { CATEGORY_BADGE } from "@/components/okr/okrConstants";
import { ContributorStack } from "@/components/okr/ContributorStack";

interface PppRowProps {
  ppp: Ppp;
  /** Position in the ranked list, or null when the visible list is filtered or truncated. */
  rank: number | null;
  /** Leader and above: may open the edit form (BE `OKR_LEADER_ONLY`). */
  canEdit: boolean;
  canDelete: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  onEdit: (ppp: Ppp) => void;
  onDelete: (ppp: Ppp) => void;
}

const MAX_KR_CHIPS = 3;

export function PppRow({ ppp, rank, canEdit, canDelete, dragHandleProps, onEdit, onDelete }: PppRowProps) {
  const extraKrs = ppp.keyResults.length - MAX_KR_CHIPS;

  return (
    <div
      className="group relative grid min-h-[56px] items-center gap-3 border-b border-border/40 px-4 transition-colors hover:bg-muted/20"
      style={PPP_ROW_COLS}
    >
      <div className={cn("absolute inset-y-2 left-0 w-[3px] rounded-r", PPP_TYPE_DOT[ppp.type])} />

      {/* Rank + grip */}
      <div className="flex items-center gap-1">
        {dragHandleProps ? (
          <div {...dragHandleProps} className="cursor-grab rounded p-0.5 text-muted-foreground/40 hover:text-muted-foreground active:cursor-grabbing" aria-label="Drag to rank">
            <GripVertical className="h-4 w-4" />
          </div>
        ) : <div className="w-5" />}
        <span className="w-6 text-right font-mono text-xs tabular-nums text-muted-foreground">{rank ?? "—"}</span>
      </div>

      {/* Type + name */}
      <div className="flex min-w-0 items-center gap-2">
        <span className={cn("inline-flex shrink-0 items-center rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide", PPP_TYPE_BADGE[ppp.type])}>
          {ppp.type}
        </span>
        <Link to={`/ppp/${ppp.pid}`} className="min-w-0 truncate text-sm font-medium hover:underline">
          {ppp.name}
        </Link>
        {ppp.isConfidential && <PppLock />}
        <ContributorStack contributors={ppp.members} count={ppp.members.length} noun="member" />
        {ppp.keyResults.length === 0 && canEdit && (
          <span title="No Key Result linked — edit to fix" className="shrink-0 text-amber-500">
            <AlertTriangle className="h-3.5 w-3.5" />
          </span>
        )}
      </div>

      {/* Category */}
      <div>
        {ppp.category && (
          <span className={cn("inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide", CATEGORY_BADGE[ppp.category])}>
            {PPP_CATEGORY_LABEL[ppp.category]}
          </span>
        )}
      </div>

      {/* KR chips */}
      <div className="flex min-w-0 flex-wrap items-center gap-1">
        {ppp.keyResults.slice(0, MAX_KR_CHIPS).map((kr) => (
          <Badge key={kr.pid} variant="outline" className="font-mono text-[10px] font-normal" title={kr.name}>{kr.code}</Badge>
        ))}
        {extraKrs > 0 && <span className="text-[11px] text-muted-foreground">+{extraKrs}</span>}
      </div>

      {/* DRI */}
      <div className="flex min-w-0 items-center gap-1.5">
        {ppp.dri ? (
          <>
            <UserAvatar src={ppp.dri.avatarUrl} name={ppp.dri.fullName} className="h-[22px] w-[22px] shrink-0" fallbackClassName="text-[9px]" />
            <span className="truncate text-xs text-muted-foreground">{ppp.dri.fullName}</span>
          </>
        ) : <span className="text-xs text-muted-foreground">—</span>}
      </div>

      {/* Deliverables */}
      <Link to={`/priorities?pppPids=${ppp.pid}`} className="text-xs text-muted-foreground hover:text-foreground hover:underline">
        {ppp.deliverableCount} deliverable{ppp.deliverableCount === 1 ? "" : "s"}
      </Link>

      {/* Menu */}
      <div className="flex justify-end">
        {(canEdit || canDelete) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Actions for ${ppp.name}`}><MoreHorizontal className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canEdit && <DropdownMenuItem onClick={() => onEdit(ppp)}><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>}
              {canDelete && (
                <DropdownMenuItem className="text-destructive" onClick={() => onDelete(ppp)}>
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
